import { z } from 'zod';
import { CrmInboundChannel } from './crm';

export const CrmPartyMergeFieldPick = z.enum(['survivor', 'merged']);
export type CrmPartyMergeFieldPick = z.infer<typeof CrmPartyMergeFieldPick>;

export const CrmPartyMergeFieldResolutions = z.object({
  displayName: CrmPartyMergeFieldPick,
  primaryCityId: CrmPartyMergeFieldPick.optional(),
});
export type CrmPartyMergeFieldResolutions = z.infer<typeof CrmPartyMergeFieldResolutions>;

export const CrmPartyMergeBody = z.object({
  survivorPartyId: z.uuid(),
  mergedPartyId: z.uuid(),
  fieldResolutions: CrmPartyMergeFieldResolutions,
});
export type CrmPartyMergeBody = z.infer<typeof CrmPartyMergeBody>;

export const CrmPartyMergeResultDto = z.object({
  ok: z.literal(true),
  survivorPartyId: z.uuid(),
  mergeLogId: z.uuid(),
});
export type CrmPartyMergeResultDto = z.infer<typeof CrmPartyMergeResultDto>;

export const CrmPartySearchQuery = z.object({
  q: z.string().trim().min(2).max(120),
  excludePartyId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(20).default(10),
});
export type CrmPartySearchQuery = z.infer<typeof CrmPartySearchQuery>;

export const CrmPartySearchHitDto = z.object({
  partyId: z.uuid().nullable(),
  inboundChannel: CrmInboundChannel,
  contactUserId: z.uuid(),
  manyChatContactId: z.uuid().optional(),
  label: z.string().min(1).max(200),
  subtitle: z.string().max(200).nullable(),
});
export type CrmPartySearchHitDto = z.infer<typeof CrmPartySearchHitDto>;

export const CrmPartySearchResultDto = z.object({
  items: z.array(CrmPartySearchHitDto),
});
export type CrmPartySearchResultDto = z.infer<typeof CrmPartySearchResultDto>;

export const CrmPartyResolveContactQuery = z.object({
  inboundChannel: CrmInboundChannel,
  contactUserId: z.uuid(),
  manyChatContactId: z.uuid().optional(),
});
export type CrmPartyResolveContactQuery = z.infer<typeof CrmPartyResolveContactQuery>;

export const CrmPartyResolveContactDto = z.object({
  partyId: z.uuid(),
});
export type CrmPartyResolveContactDto = z.infer<typeof CrmPartyResolveContactDto>;

export const CrmPartyMergeSideStatsDto = z.object({
  contactLinks: z.number().int().nonnegative(),
  conversations: z.number().int().nonnegative(),
  managerNotes: z.number().int().nonnegative(),
  manyChatInquiries: z.number().int().nonnegative(),
  deals: z.number().int().nonnegative(),
});
export type CrmPartyMergeSideStatsDto = z.infer<typeof CrmPartyMergeSideStatsDto>;

export const CrmPartyMergeSideDto = z.object({
  partyId: z.uuid(),
  displayName: z.string().nullable(),
  primaryCityId: z.uuid().nullable(),
  stats: CrmPartyMergeSideStatsDto,
  links: z.array(
    z.object({
      channel: z.string(),
      label: z.string().nullable(),
      externalParticipantId: z.string(),
    }),
  ),
});
export type CrmPartyMergeSideDto = z.infer<typeof CrmPartyMergeSideDto>;

export const CrmPartyMergePreviewDto = z.object({
  survivor: CrmPartyMergeSideDto,
  merged: CrmPartyMergeSideDto,
  conflicts: z.object({
    displayName: z.boolean(),
    primaryCityId: z.boolean(),
  }),
});
export type CrmPartyMergePreviewDto = z.infer<typeof CrmPartyMergePreviewDto>;

export const CrmPartyMergePreviewQuery = z.object({
  survivorPartyId: z.uuid(),
  mergedPartyId: z.uuid(),
});
export type CrmPartyMergePreviewQuery = z.infer<typeof CrmPartyMergePreviewQuery>;
